"use client";

import { useState } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { Euro } from 'lucide-react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';

interface EuroAmountFieldProps {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  showIcon?: boolean;
  className?: string;
}

const addThousandsSeparator = (integerDigits: string): string => (
  integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
);

interface EuroInputParts {
  integerPart: string;
  decimalPart: string;
  hasDecimal: boolean;
}

const splitEuroInput = (input: string): EuroInputParts => {
  const cleaned = input.replace(/[^\d.,]/g, '');
  if (!cleaned) {
    return { integerPart: '', decimalPart: '', hasDecimal: false };
  }

  if (cleaned.includes(',')) {
    const commaIndex = cleaned.lastIndexOf(',');
    const integerPart = cleaned.slice(0, commaIndex).replace(/[^\d]/g, '');
    const decimalPart = cleaned.slice(commaIndex + 1).replace(/[^\d]/g, '').slice(0, 2);
    return { integerPart, decimalPart, hasDecimal: true };
  }

  const dotCount = (cleaned.match(/\./g) || []).length;
  if (dotCount === 1) {
    const [left = '', right = ''] = cleaned.split('.');
    const decimalDigits = right.replace(/[^\d]/g, '');
    if (decimalDigits.length <= 2) {
      return {
        integerPart: left.replace(/[^\d]/g, ''),
        decimalPart: decimalDigits,
        hasDecimal: true,
      };
    }
  }

  return {
    integerPart: cleaned.replace(/[^\d]/g, ''),
    decimalPart: '',
    hasDecimal: false,
  };
};

const normalizeIntegerPart = (value: string): string => (
  value.replace(/^0+(?=\d)/, '') || '0'
);

const formatEuroInputForEditing = (input: string): string => {
  const { integerPart, decimalPart, hasDecimal } = splitEuroInput(input);
  if (!integerPart && !decimalPart && !hasDecimal) return '';

  const normalizedInteger = normalizeIntegerPart(integerPart || '0');

  if (!hasDecimal) {
    return normalizedInteger;
  }

  return decimalPart.length
    ? `${normalizedInteger},${decimalPart}`
    : `${normalizedInteger},`;
};

const formatEuroAmount = (rawValue: string): string => {
  const cleaned = rawValue.replace(/[^\d.]/g, '');
  if (!cleaned) return '';

  const [integerPartRaw, decimalPartRaw = ''] = cleaned.split('.');
  const integerPart = normalizeIntegerPart(integerPartRaw);
  const decimalPart = decimalPartRaw.padEnd(2, '0').slice(0, 2);
  const withSeparators = addThousandsSeparator(integerPart);
  return `${withSeparators},${decimalPart}`;
};

const parseEuroAmount = (displayValue: string): string => {
  const { integerPart, decimalPart, hasDecimal } = splitEuroInput(displayValue);
  if (!integerPart && !decimalPart) return '';

  const normalizedInteger = normalizeIntegerPart(integerPart || '0');
  const normalizedDecimal = hasDecimal
    ? decimalPart.slice(0, 2).padEnd(2, '0')
    : '00';

  return `${normalizedInteger}.${normalizedDecimal}`;
};

export function EuroAmountField({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  hideLabel = false,
  showIcon = true,
  className,
}: EuroAmountFieldProps) {
  const { control } = useFormContext();
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const normalizedValue = field.value === null || field.value === undefined
          ? ''
          : String(field.value);
        const formattedValue = normalizedValue ? formatEuroAmount(normalizedValue) : '';
        const displayValue = isFocused ? inputValue : formattedValue;

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
          const nextDisplayValue = formatEuroInputForEditing(event.target.value);
          const rawValue = parseEuroAmount(nextDisplayValue);

          setInputValue(nextDisplayValue);
          if (!rawValue) {
            field.onChange('');
            return;
          }
          field.onChange(rawValue);
        };

        const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
          setIsFocused(false);
          field.onBlur();

          const nextDisplayValue = formatEuroInputForEditing(event.target.value);
          const rawValue = parseEuroAmount(nextDisplayValue);
          if (!rawValue) {
            setInputValue('');
            field.onChange('');
            return;
          }

          field.onChange(rawValue);
          setInputValue(nextDisplayValue);
        };

        return (
          <FormItem className="flex flex-col">
            <FormLabel className={cn(hideLabel && "sr-only")}>
              {label} {!hideLabel && required && '*'}
            </FormLabel>
            <InputGroup data-disabled={disabled ? true : undefined}>
              <FormControl>
                <InputGroupInput
                  type="text"
                  name={field.name}
                  ref={field.ref}
                  inputMode="decimal"
                  placeholder={placeholder}
                  readOnly={disabled}
                  className={cn(
                    disabled && "bg-muted opacity-50 cursor-text select-text",
                    className
                  )}
                  value={displayValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => {
                    setIsFocused(true);
                    setInputValue(formatEuroInputForEditing(normalizedValue));
                  }}
                />
              </FormControl>
              {showIcon && (
                <InputGroupAddon align="inline-start">
                  <Euro className="h-4 w-4" />
                </InputGroupAddon>
              )}
            </InputGroup>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
